import type ExcelJS from 'exceljs';
import JSZip from 'jszip';
import {
  CellValue,
  ColumnKind,
  ColumnProfile,
  GroupingInsight,
  RowRecord,
  SheetAnalysis,
  SheetDataset,
  WorkbookAnalysis,
} from '../types/analytics';

const EMPTY_LABEL = 'Not provided';
const WORKSHEET_RELATIONSHIP_TYPE = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet';

export async function parseWorkbook(file: File): Promise<WorkbookAnalysis> {
  if (file.name.toLowerCase().endsWith('.csv')) {
    return parseCsv(file);
  }

  const buffer = await file.arrayBuffer();
  try {
    return await parseExcelJsWorkbook(file.name, buffer);
  } catch (error) {
    return parseOoxmlWorkbook(file.name, buffer, error);
  }
}

async function parseExcelJsWorkbook(fileName: string, buffer: ArrayBuffer): Promise<WorkbookAnalysis> {
  const ExcelJSModule = await import('exceljs');
  const workbook = new ExcelJSModule.default.Workbook();
  await workbook.xlsx.load(buffer);
  const sheets = workbook.worksheets.map((worksheet) => {
    const rawHeaderValues = worksheet.getRow(1).values as unknown;
    const headerValues: ExcelJS.CellValue[] = Array.isArray(rawHeaderValues) ? rawHeaderValues : [];
    const columns = makeUniqueHeaders(headerValues
      .slice(1)
      .map((value: ExcelJS.CellValue, index: number) => formatHeader(cellToValue(value), index))
      .filter(Boolean));

    const rows: RowRecord[] = [];
    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber === 1) return;

      const record: RowRecord = {};
      columns.forEach((column: string, index: number) => {
        record[column] = cellToValue(row.getCell(index + 1).value);
      });

      if (Object.values(record).some((value) => !isBlank(value))) {
        rows.push(record);
      }
    });

    return analyseSheet({ name: worksheet.name, rows, columns });
  }).filter((sheet) => sheet.rowCount > 0 || sheet.columnCount > 0);

  return {
    fileName,
    sheets,
  };
}

async function parseCsv(file: File): Promise<WorkbookAnalysis> {
  const text = await file.text();
  const [headers = [], ...records] = parseCsvRows(text);
  const columns = makeUniqueHeaders(headers.map((header, index) => formatHeader(header, index)));
  const rows = records
    .map((record) =>
      columns.reduce<RowRecord>((row, column, index) => {
        row[column] = normaliseCell(record[index] ?? null);
        return row;
      }, {}),
    )
    .filter((row) => Object.values(row).some((value) => !isBlank(value)));

  return {
    fileName: file.name,
    sheets: [analyseSheet({ name: 'CSV import', rows, columns })],
  };
}

async function parseOoxmlWorkbook(
  fileName: string,
  buffer: ArrayBuffer,
  originalError: unknown,
): Promise<WorkbookAnalysis> {
  try {
    const archive = await JSZip.loadAsync(buffer);
    const workbookXml = await readZipText(archive, 'xl/workbook.xml');
    const relationshipsXml = await readZipText(archive, 'xl/_rels/workbook.xml.rels');
    const sharedStringsXml = await readOptionalZipText(archive, 'xl/sharedStrings.xml');
    const sharedStrings = sharedStringsXml ? parseSharedStrings(sharedStringsXml) : [];
    const relationships = parseWorkbookRelationships(relationshipsXml);
    const workbookSheets = parseWorkbookSheets(workbookXml);

    const sheets = (
      await Promise.all(
        workbookSheets.map(async (sheet) => {
          const target = relationships.get(sheet.relationshipId);
          if (!target) return null;

          const worksheetXml = await readOptionalZipText(archive, normaliseZipPath(target));
          if (!worksheetXml) return null;

          return parseWorksheetXml(sheet.name, worksheetXml, sharedStrings);
        }),
      )
    ).filter((sheet): sheet is SheetAnalysis => Boolean(sheet && (sheet.rowCount > 0 || sheet.columnCount > 0)));

    return {
      fileName,
      sheets,
    };
  } catch (fallbackError) {
    throw new Error(
      `The workbook could not be analysed. ${describeParserError(fallbackError) || describeParserError(originalError)}`,
      { cause: fallbackError },
    );
  }
}

function parseWorkbookSheets(xml: string): Array<{ name: string; relationshipId: string }> {
  return [...xml.matchAll(/<[^:>\s]*:?sheet\b([^>]*)\/?>/gi)]
    .map((match) => ({
      name: decodeXml(readXmlAttribute(match[1], 'name') || 'Sheet'),
      relationshipId: readXmlAttribute(match[1], 'id') || '',
    }))
    .filter((sheet) => sheet.relationshipId);
}

function parseWorkbookRelationships(xml: string): Map<string, string> {
  const relationships = new Map<string, string>();

  [...xml.matchAll(/<[^:>\s]*:?Relationship\b([^>]*)\/?>/gi)].forEach((match) => {
    const type = readXmlAttribute(match[1], 'Type');
    const id = readXmlAttribute(match[1], 'Id');
    const target = readXmlAttribute(match[1], 'Target');

    if (id && target && type === WORKSHEET_RELATIONSHIP_TYPE) {
      relationships.set(id, target);
    }
  });

  return relationships;
}

function parseSharedStrings(xml: string): string[] {
  return [...xml.matchAll(/<[^:>\s]*:?si\b[^>]*>([\s\S]*?)<\/[^:>\s]*:?si>/gi)].map((match) =>
    decodeXml([...match[1].matchAll(/<[^:>\s]*:?t\b[^>]*>([\s\S]*?)<\/[^:>\s]*:?t>/gi)].map((textMatch) => textMatch[1]).join('')),
  );
}

function parseWorksheetXml(name: string, xml: string, sharedStrings: string[]): SheetAnalysis {
  const parsedRows = [...xml.matchAll(/<[^:>\s]*:?row\b[^>]*>([\s\S]*?)<\/[^:>\s]*:?row>/gi)]
    .map((rowMatch) => parseWorksheetRow(rowMatch[1], sharedStrings))
    .filter((row) => row.some((value) => !isBlank(value)));
  const [headerValues = [], ...dataRows] = parsedRows;
  const columns = makeUniqueHeaders(headerValues.map((value, index) => formatHeader(value, index)));
  const rows = dataRows
    .map((values) =>
      columns.reduce<RowRecord>((record, column, index) => {
        record[column] = values[index] ?? null;
        return record;
      }, {}),
    )
    .filter((row) => Object.values(row).some((value) => !isBlank(value)));

  return analyseSheet({ name, rows, columns });
}

function parseWorksheetRow(xml: string, sharedStrings: string[]): CellValue[] {
  const values: CellValue[] = [];

  [...xml.matchAll(/<[^:>\s]*:?c\b([^>]*)(?:\/>|>([\s\S]*?)<\/[^:>\s]*:?c>)/gi)].forEach((cellMatch) => {
    const attributes = cellMatch[1];
    const cellXml = cellMatch[2] ?? '';
    const reference = readXmlAttribute(attributes, 'r');
    const columnIndex = reference ? columnReferenceToIndex(reference) : values.length;
    values[columnIndex] = parseWorksheetCell(attributes, cellXml, sharedStrings);
  });

  return values.map((value) => value ?? null);
}

function parseWorksheetCell(attributes: string, xml: string, sharedStrings: string[]): CellValue {
  const type = readXmlAttribute(attributes, 't');

  if (type === 'inlineStr') {
    return normaliseCell(readInlineString(xml));
  }

  const rawValue = readXmlNode(xml, 'v');
  if (!rawValue) return null;

  const decodedValue = decodeXml(rawValue);

  if (type === 's') {
    return normaliseCell(sharedStrings[Number(decodedValue)] ?? decodedValue);
  }

  if (type === 'b') {
    return decodedValue === '1';
  }

  if (type === 'str') {
    return normaliseCell(decodedValue);
  }

  const numericValue = Number(decodedValue);
  return Number.isFinite(numericValue) ? numericValue : normaliseCell(decodedValue);
}

function readInlineString(xml: string): string {
  return [...xml.matchAll(/<[^:>\s]*:?t\b[^>]*>([\s\S]*?)<\/[^:>\s]*:?t>/gi)].map((match) => match[1]).join('');
}

function readXmlNode(xml: string, nodeName: string): string {
  const match = new RegExp(`<[^:>\\s]*:?${nodeName}\\b[^>]*>([\\s\\S]*?)<\\/[^:>\\s]*:?${nodeName}>`, 'i').exec(xml);
  return match?.[1] ?? '';
}

function readXmlAttribute(attributes: string, attributeName: string): string {
  const match = new RegExp(`(?:^|\\s)(?:[^:=\\s]+:)?${attributeName}=(["'])(.*?)\\1`, 'i').exec(attributes);
  return match?.[2] ?? '';
}

function decodeXml(value: string): string {
  return value
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .replace(/&#(\d+);/g, (_, code: string) => String.fromCodePoint(Number(code)))
    .replace(/&#x([0-9a-f]+);/gi, (_, code: string) => String.fromCodePoint(Number.parseInt(code, 16)));
}

function columnReferenceToIndex(reference: string): number {
  const column = reference.match(/[A-Z]+/i)?.[0].toUpperCase() ?? '';
  return [...column].reduce((total, char) => total * 26 + char.charCodeAt(0) - 64, 0) - 1;
}

function normaliseZipPath(target: string): string {
  const cleanTarget = target.replace(/^\/+/, '');
  return cleanTarget.startsWith('xl/') ? cleanTarget : `xl/${cleanTarget}`;
}

async function readZipText(archive: JSZip, path: string): Promise<string> {
  const file = archive.file(path);
  if (!file) throw new Error(`Missing ${path}`);
  return file.async('text');
}

async function readOptionalZipText(archive: JSZip, path: string): Promise<string | null> {
  const file = archive.file(path);
  return file ? file.async('text') : null;
}

function makeUniqueHeaders(headers: string[]): string[] {
  const seen = new Map<string, number>();

  return headers.map((header, index) => {
    const base = header || `Column ${index + 1}`;
    const count = seen.get(base) ?? 0;
    seen.set(base, count + 1);
    return count ? `${base} ${count + 1}` : base;
  });
}

function describeParserError(error: unknown): string {
  return error instanceof Error ? error.message : '';
}

export function analyseSheet(dataset: SheetDataset): SheetAnalysis {
  const profiles = dataset.columns.map((column) => profileColumn(column, dataset.rows));
  const numericProfiles = profiles.filter((profile) => profile.kind === 'numeric');
  const categoricalProfiles = profiles.filter((profile) =>
    ['categorical', 'boolean', 'date'].includes(profile.kind),
  );
  const missingCells = profiles.reduce((total, profile) => total + profile.missing, 0);
  const totalCells = Math.max(dataset.rows.length * Math.max(dataset.columns.length, 1), 1);
  const duplicateRows = countDuplicateRows(dataset.rows);
  const completeness = 1 - missingCells / totalCells;
  const uniquenessPenalty = dataset.rows.length ? duplicateRows / dataset.rows.length : 0;
  const qualityScore = Math.round(Math.max(0, Math.min(1, completeness - uniquenessPenalty * 0.25)) * 100);
  const groupings = buildGroupings(categoricalProfiles, dataset.rows.length);

  return {
    dataset,
    rowCount: dataset.rows.length,
    columnCount: dataset.columns.length,
    profiles,
    numericProfiles,
    categoricalProfiles,
    qualityScore,
    missingCells,
    duplicateRows,
    summary: buildSummary(dataset, profiles, qualityScore, duplicateRows),
    groupings,
  };
}

function formatHeader(value: CellValue, index: number): string {
  const label = formatValue(value).trim();
  return label || `Column ${index + 1}`;
}

function cellToValue(value: ExcelJS.CellValue | undefined): CellValue {
  if (value === undefined || value === null) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return normaliseCell(value);
  }
  if (typeof value === 'object') {
    if ('result' in value) return cellToValue(value.result as ExcelJS.CellValue);
    if ('text' in value) return normaliseCell(value.text);
    if ('richText' in value) {
      return normaliseCell(value.richText.map((part) => part.text).join(''));
    }
    if ('hyperlink' in value && 'text' in value) return normaliseCell(value.text);
  }
  return normaliseCell(String(value));
}

function parseCsvRows(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let quoted = false;

  for (let index = 0; index < text.length; index += 1) {
    const char = text[index];
    const next = text[index + 1];

    if (char === '"' && quoted && next === '"') {
      field += '"';
      index += 1;
    } else if (char === '"') {
      quoted = !quoted;
    } else if (char === ',' && !quoted) {
      row.push(field);
      field = '';
    } else if ((char === '\n' || char === '\r') && !quoted) {
      if (char === '\r' && next === '\n') index += 1;
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
    } else {
      field += char;
    }
  }

  row.push(field);
  rows.push(row);
  return rows.filter((currentRow) => currentRow.some((value) => value.trim()));
}

function profileColumn(name: string, rows: RowRecord[]): ColumnProfile {
  const values = rows.map((row) => normaliseCell(row[name]));
  const filledValues = values.filter((value) => !isBlank(value));
  const missing = values.length - filledValues.length;
  const valueCounts = countValues(filledValues);
  const examples = [...new Set(filledValues.map(formatValue))].slice(0, 4);
  const numericValues = filledValues.map(toNumber).filter((value): value is number => value !== null);
  const dateValues = filledValues.filter(isDateLike);
  const booleanValues = filledValues.filter((value) => typeof value === 'boolean');
  const kind = detectKind(filledValues, numericValues, dateValues, booleanValues);
  const topValues = [...valueCounts.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8)
    .map(([label, count]) => ({
      label,
      count,
      share: rows.length ? Math.round((count / rows.length) * 100) : 0,
    }));

  const base: ColumnProfile = {
    name,
    kind,
    filled: filledValues.length,
    missing,
    unique: valueCounts.size,
    completeness: rows.length ? Math.round((filledValues.length / rows.length) * 100) : 0,
    examples,
    topValues,
  };

  if (kind === 'numeric' && numericValues.length) {
    const total = numericValues.reduce((sum, value) => sum + value, 0);
    return {
      ...base,
      min: Math.min(...numericValues),
      max: Math.max(...numericValues),
      average: total / numericValues.length,
      total,
    };
  }

  return base;
}

function detectKind(
  values: CellValue[],
  numericValues: number[],
  dateValues: CellValue[],
  booleanValues: boolean[],
): ColumnKind {
  if (!values.length) return 'empty';

  const threshold = Math.max(1, values.length * 0.8);
  if (numericValues.length >= threshold) return 'numeric';
  if (dateValues.length >= threshold) return 'date';
  if (booleanValues.length >= threshold) return 'boolean';

  const uniqueCount = new Set(values.map(formatValue)).size;
  if (uniqueCount <= Math.max(20, values.length * 0.5)) return 'categorical';

  return 'mixed';
}

function buildSummary(
  dataset: SheetDataset,
  profiles: ColumnProfile[],
  qualityScore: number,
  duplicateRows: number,
): string[] {
  const strongestCategory = profiles
    .filter((profile) => profile.kind === 'categorical' && profile.topValues.length)
    .sort((a, b) => b.topValues[0].share - a.topValues[0].share)[0];
  const bestMetric = profiles
    .filter((profile) => profile.kind === 'numeric' && typeof profile.average === 'number')
    .sort((a, b) => (b.filled || 0) - (a.filled || 0))[0];
  const sparseColumns = profiles.filter((profile) => profile.completeness < 70);

  const summary = [
    `${dataset.name} contains ${formatInteger(dataset.rows.length)} records across ${formatInteger(dataset.columns.length)} fields.`,
    `The automated quality score is ${qualityScore}%, based on completeness and duplicate density.`,
  ];

  if (bestMetric && typeof bestMetric.average === 'number') {
    summary.push(
      `${bestMetric.name} is the strongest numeric signal, averaging ${formatNumber(bestMetric.average)} across ${formatInteger(bestMetric.filled)} populated rows.`,
    );
  }

  if (strongestCategory?.topValues[0]) {
    summary.push(
      `${strongestCategory.name} is a clear grouping factor: ${strongestCategory.topValues[0].label} represents ${strongestCategory.topValues[0].share}% of all rows.`,
    );
  }

  if (sparseColumns.length) {
    summary.push(`${sparseColumns.length} field${sparseColumns.length === 1 ? '' : 's'} need attention because completeness is below 70%.`);
  }

  if (duplicateRows) {
    summary.push(`${formatInteger(duplicateRows)} likely duplicate row${duplicateRows === 1 ? '' : 's'} were detected.`);
  }

  return summary;
}

function buildGroupings(profiles: ColumnProfile[], rowCount: number): GroupingInsight[] {
  return profiles
    .filter((profile) => profile.topValues.length >= 2 && profile.unique <= Math.max(12, rowCount * 0.45))
    .sort((a, b) => b.filled - a.filled)
    .slice(0, 5)
    .map((profile) => ({
      column: profile.name,
      groups: profile.topValues.slice(0, 6),
    }));
}

function countValues(values: CellValue[]): Map<string, number> {
  const counts = new Map<string, number>();
  values.forEach((value) => {
    const key = formatValue(value) || EMPTY_LABEL;
    counts.set(key, (counts.get(key) ?? 0) + 1);
  });
  return counts;
}

function countDuplicateRows(rows: RowRecord[]): number {
  const seen = new Set<string>();
  let duplicates = 0;

  rows.forEach((row) => {
    const signature = JSON.stringify(row, Object.keys(row).sort());
    if (seen.has(signature)) {
      duplicates += 1;
    } else {
      seen.add(signature);
    }
  });

  return duplicates;
}

function normaliseCell(value: unknown): CellValue {
  if (value === undefined || value === null) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'number' || typeof value === 'boolean') return value;
  if (typeof value === 'string') return value.trim() || null;
  return String(value);
}

function toNumber(value: CellValue): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value !== 'string') return null;

  const clean = value.replace(/[$£€,]/g, '').trim();
  if (!clean) return null;

  const parsed = Number(clean);
  return Number.isFinite(parsed) ? parsed : null;
}

function isDateLike(value: CellValue): boolean {
  if (value instanceof Date && !Number.isNaN(value.getTime())) return true;
  if (typeof value !== 'string') return false;
  if (!/[/-]/.test(value)) return false;

  const parsed = Date.parse(value);
  return Number.isFinite(parsed);
}

function isBlank(value: CellValue): boolean {
  return value === null || value === '';
}

export function formatValue(value: CellValue): string {
  if (value === null) return '';
  if (value instanceof Date) return new Intl.DateTimeFormat('en-GB').format(value);
  return String(value);
}

export function formatInteger(value: number): string {
  return new Intl.NumberFormat('en-GB', { maximumFractionDigits: 0 }).format(value);
}

export function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-GB', {
    maximumFractionDigits: Math.abs(value) >= 100 ? 0 : 2,
  }).format(value);
}
